import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Loader2, Zap, AlertCircle, Search, Globe, Filter, Hash, Play, Link2 } from 'lucide-react';
import { Can } from '../../lib/rbac';

// ─────────────────────────────────────────────────
// Site flags for market chips
// ─────────────────────────────────────────────────
const SITE_OPTIONS: { id: string; label: string; flag: string }[] = [
  { id: 'MLA', label: 'Argentina', flag: '🇦🇷' },
  { id: 'MLM', label: 'Mexico', flag: '🇲🇽' },
  { id: 'MLC', label: 'Chile', flag: '🇨🇱' },
  { id: 'MCO', label: 'Colombia', flag: '🇨🇴' },
  { id: 'MLU', label: 'Uruguay', flag: '🇺🇾' },
  { id: 'MLB', label: 'Brasil', flag: '🇧🇷' },
];

const INTERVAL_OPTIONS = [
  { value: 120, label: 'Cada 2 horas' },
  { value: 240, label: 'Cada 4 horas' },
  { value: 360, label: 'Cada 6 horas' },
  { value: 720, label: 'Cada 12 horas' },
  { value: 1440, label: 'Cada 24 horas' },
];

const SEARCH_METHOD_OPTIONS = [
  { value: 'gtin_first', label: 'GTIN primero, luego texto', help: 'Busca por codigo de barras (GTIN/EAN). Si no encuentra, busca por texto. Mejor precision.' },
  { value: 'text_only', label: 'Solo texto', help: 'Busca en Amazon solo por titulo del producto. Mas rapido pero menos preciso.' },
  { value: 'gtin_only', label: 'Solo GTIN', help: 'Solo busca por codigo de barras. Ignora productos sin GTIN. Maxima precision, menor cobertura.' },
];

interface TrendsConfigPanelProps {
  sellerId: number;
  proc: any;
  lastStats: any;
  formatDuration: (ms: number | undefined) => string;
  onRunNow: () => void;
  running: boolean;
  showToast: (msg: string, ok: boolean) => void;
}

export function TrendsDiscoveryConfigPanel({ sellerId, proc, lastStats, formatDuration, onRunNow, running, showToast }: TrendsConfigPanelProps) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState<Record<string, any>>({});

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<any>(`/scheduler/trends_discovery/settings?seller_id=${sellerId}`);
        if (res.success && res.data) {
          setConfig(res.data);
          setEdits(res.data);
        }
      } catch (err) {
        console.error('Failed to load trends config:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [sellerId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/scheduler/trends_discovery/settings?seller_id=${sellerId}`, { settings: edits });
      if (res.success) {
        setConfig(edits);
        showToast('Configuracion de tendencias guardada', true);
      } else {
        showToast((res as any).error || 'Error al guardar configuracion', false);
      }
    } catch {
      showToast('Error al guardar configuracion', false);
    }
    setSaving(false);
  };

  const update = (field: string, value: any) => setEdits(e => ({ ...e, [field]: value }));

  const toggleSite = (siteId: string) => {
    const current: string[] = edits.sites || [];
    if (current.includes(siteId)) {
      update('sites', current.filter((s: string) => s !== siteId));
    } else {
      update('sites', [...current, siteId]);
    }
  };

  const inputCls = 'w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm';
  const labelCls = 'block text-xs font-medium text-[var(--color-text-secondary)] mb-1';
  const helpCls = 'text-[10px] text-[var(--color-text-tertiary)] mt-0.5';
  const sectionCls = 'mb-5';
  const sectionTitleCls = 'text-xs font-semibold text-[var(--color-text-secondary)] mb-2 flex items-center gap-1.5';

  if (loading) {
    return (
      <div className="px-4 py-6 bg-[var(--color-bg-tertiary)] border-t border-[var(--color-border)] flex justify-center">
        <Loader2 size={20} className="animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="px-4 py-4 bg-[var(--color-bg-tertiary)] border-t border-[var(--color-border)]">
        <p className="text-sm text-red-400">No se pudo cargar la configuracion de tendencias</p>
      </div>
    );
  }

  const selectedMethod = SEARCH_METHOD_OPTIONS.find(o => o.value === (edits.search_method || 'gtin_first'));

  return (
    <div className="px-4 py-4 bg-[var(--color-bg-tertiary)] border-t border-[var(--color-border)]">
      <h4 className="text-xs font-semibold text-[var(--color-text-tertiary)] uppercase tracking-wider mb-4">
        Configuracion de Tendencias
      </h4>

      {/* Frecuencia */}
      <div className={sectionCls}>
        <div className={sectionTitleCls}>
          <Play size={12} /> Frecuencia
        </div>
        <div className="max-w-xs">
          <select
            value={edits.interval_minutes ?? 360}
            onChange={e => update('interval_minutes', parseInt(e.target.value))}
            className={inputCls}
          >
            {INTERVAL_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <p className={helpCls}>Con que frecuencia se buscan nuevas tendencias en MercadoLibre</p>
        </div>
      </div>

      {/* Mercados */}
      <div className={sectionCls}>
        <div className={sectionTitleCls}>
          <Globe size={12} /> Mercados
        </div>
        <div className="flex flex-wrap gap-2">
          {SITE_OPTIONS.map(site => {
            const selected = (edits.sites || []).includes(site.id);
            return (
              <button
                key={site.id}
                onClick={() => toggleSite(site.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  selected
                    ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
                    : 'bg-[var(--color-bg)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:border-[var(--color-primary)]'
                }`}
              >
                {site.flag} {site.id}
              </button>
            );
          })}
        </div>
        <p className={helpCls}>En que paises de MercadoLibre buscar tendencias. Selecciona al menos uno.</p>
      </div>

      {/* Que buscar */}
      <div className={sectionCls}>
        <div className={sectionTitleCls}>
          <Search size={12} /> Que buscar
        </div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={edits.use_general_trends !== false}
              onChange={e => update('use_general_trends', e.target.checked)}
              className="rounded border-[var(--color-border)]"
            />
            <span className="text-xs text-[var(--color-text)]">Tendencias generales</span>
          </label>
          <p className={helpCls + ' ml-5'}>Busca los productos mas vendidos y populares del momento en cada mercado</p>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={edits.use_category_trends !== false}
              onChange={e => update('use_category_trends', e.target.checked)}
              className="rounded border-[var(--color-border)]"
            />
            <span className="text-xs text-[var(--color-text)]">Tendencias por categoria</span>
          </label>
          {edits.use_category_trends !== false && (
            <div className="ml-5 max-w-[200px]">
              <label className={labelCls}>Categorias top por sitio</label>
              <select
                value={edits.top_categories_per_site ?? 5}
                onChange={e => update('top_categories_per_site', parseInt(e.target.value))}
                className={inputCls}
              >
                {[3, 5, 8, 10, 15].map(n => (
                  <option key={n} value={n}>{n} categorias</option>
                ))}
              </select>
              <p className={helpCls}>Cuantas categorias principales analizar en cada mercado</p>
            </div>
          )}
        </div>
      </div>

      {/* Filtros de producto */}
      <div className={sectionCls}>
        <div className={sectionTitleCls}>
          <Filter size={12} /> Filtros de producto
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className={labelCls}>Precio min (USD)</label>
            <input
              type="number"
              step="0.01"
              min={0}
              value={edits.min_price_usd ?? ''}
              onChange={e => update('min_price_usd', e.target.value === '' ? null : parseFloat(e.target.value))}
              className={inputCls}
              placeholder="Sin minimo"
            />
          </div>
          <div>
            <label className={labelCls}>Precio max (USD)</label>
            <input
              type="number"
              step="0.01"
              min={0}
              value={edits.max_price_usd ?? ''}
              onChange={e => update('max_price_usd', e.target.value === '' ? null : parseFloat(e.target.value))}
              className={inputCls}
              placeholder="Sin maximo"
            />
          </div>
        </div>
        <p className={helpCls}>Rango de precios en dolares para filtrar productos descubiertos</p>
      </div>

      {/* Limites */}
      <div className={sectionCls}>
        <div className={sectionTitleCls}>
          <Hash size={12} /> Limites
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className={labelCls}>Max keywords por corrida</label>
            <input
              type="number"
              min={1}
              max={200}
              value={edits.max_keywords_per_run ?? 50}
              onChange={e => update('max_keywords_per_run', parseInt(e.target.value) || 50)}
              className={inputCls}
            />
            <p className={helpCls}>Cuantas palabras clave procesar por ejecucion</p>
          </div>
          <div>
            <label className={labelCls}>Max ASINs por corrida</label>
            <input
              type="number"
              min={1}
              max={500}
              value={edits.max_asins_per_run ?? 100}
              onChange={e => update('max_asins_per_run', parseInt(e.target.value) || 100)}
              className={inputCls}
            />
            <p className={helpCls}>Cuantos productos Amazon vincular por ejecucion</p>
          </div>
        </div>
      </div>

      {/* Accion automatica */}
      <div className={sectionCls}>
        <div className={sectionTitleCls}>
          <AlertCircle size={12} /> Accion automatica
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={edits.auto_queue === true}
            onChange={e => update('auto_queue', e.target.checked)}
            className="rounded border-[var(--color-border)]"
          />
          <span className="text-xs text-[var(--color-text)]">Encolar automaticamente al enricher</span>
        </label>
        <p className={helpCls}>Si esta activo, los ASINs descubiertos se envian automaticamente al enricher para publicacion. Si no, quedan como sugerencias para revision manual.</p>
      </div>

      {/* Vinculacion Amazon */}
      <div className={sectionCls}>
        <div className={sectionTitleCls}>
          <Link2 size={12} /> Vinculacion Amazon
        </div>
        <div className="max-w-xs">
          <select
            value={edits.search_method ?? 'gtin_first'}
            onChange={e => update('search_method', e.target.value)}
            className={inputCls}
          >
            {SEARCH_METHOD_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {selectedMethod && (
            <p className={helpCls}>{selectedMethod.help}</p>
          )}
        </div>
      </div>

      {/* Last run info */}
      {lastStats && (
        <div className="text-xs text-[var(--color-text-muted)] mb-3">
          {t('auto.lastResult')}: <span className={lastStats.status === 'ok' ? 'text-green-500' : 'text-red-400'}>{lastStats.status}</span>
          {lastStats.duration_ms && <span> · {t('auto.duration')}: {formatDuration(lastStats.duration_ms)}</span>}
          {lastStats.error && <span className="text-red-400"> · {lastStats.error}</span>}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Can cap="ai.toggle_run">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : t('auto.saveSettings')}
        </button>
        </Can>
        {proc.registered && (
          <Can cap="ai.toggle_run"><button
            onClick={onRunNow}
            disabled={running}
            className="px-4 py-2 text-sm rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] disabled:opacity-50 flex items-center gap-1.5"
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            {t('auto.runNow')}
          </button></Can>
        )}
      </div>
    </div>
  );
}
